<template>
    <div>
        <!-- `capture` opens the rear camera directly on a phone rather than the
             gallery: the point is a photo taken HERE, now, at this house --
             not one picked from yesterday's customer. The input is hidden and
             driven by the button, because a bare file input cannot be styled
             into a thumb-sized target. -->
        <input ref="file" type="file" accept="image/*" capture="environment"
            hidden @change="picked">
        <div class="d-flex flex-wrap">
            <div v-for="(p, i) in value" :key="p.url" class="thumb mr-2 mb-2">
                <v-img :src="p.url" width="96" height="96" class="grey lighten-3"></v-img>
                <v-btn fab x-small color="red" dark class="remove" :disabled="disabled"
                    @click="remove(i)">
                    <v-icon small>mdi-close</v-icon>
                </v-btn>
            </div>
        </div>
        <v-btn v-if="value.length < max" block outlined color="primary"
            :loading="shrinking" :disabled="disabled || shrinking" @click="open">
            <v-icon left>mdi-camera</v-icon>
            {{ value.length ? 'ถ่ายรูปเพิ่ม' : 'ถ่ายรูป' }} ({{ value.length }}/{{ max }})
        </v-btn>
        <div v-if="error" class="body-2 red--text mt-1">{{ error }}</div>
    </div>
</template>
<script>
import P from '@/components/proofPhoto'

function decode (file) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => resolve({ img, url })
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')) }
        img.src = url
    })
}

function toJpeg (canvas, quality) {
    return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality))
}

// A camera file (3-5 MB, 12 MP) -> a JPEG blob with a 1280 px long edge, about
// 150-300 KB. Chrome 81+ and Safari 13.1+ apply the photo's EXIF orientation
// when an <img> is decoded, so naturalWidth/Height are already the upright
// dimensions and drawImage draws it upright -- a portrait photo taken on a phone
// is not uploaded lying on its side.
async function shrink (file) {
    const { img, url } = await decode(file)
    try {
        const size = P.targetSize(img.naturalWidth, img.naturalHeight, P.MAX_EDGE)
        if (!size) throw new Error('size')
        const canvas = document.createElement('canvas')
        canvas.width = size.width
        canvas.height = size.height
        const ctx = canvas.getContext('2d')
        // JPEG has no transparency; a PNG screenshot with an alpha channel
        // would otherwise turn black where it was clear.
        ctx.fillStyle = '#fff'
        ctx.fillRect(0, 0, size.width, size.height)
        ctx.drawImage(img, 0, 0, size.width, size.height)
        let blob = null
        for (let i = 0; i < P.QUALITIES.length; i++) {
            blob = await toJpeg(canvas, P.QUALITIES[i])
            if (blob && blob.size < P.MAX_BYTES) return blob
        }
        throw new Error('too large')
    } finally {
        URL.revokeObjectURL(url)
    }
}

export default {
    // v-model: an array of { blob, url }. `url` is a local object URL for the
    // thumbnail only -- nothing is uploaded until the sheet is saved.
    props: {
        value: { type: Array, default: () => [] },
        // The stage's limit, from proofPhoto.STAGES -- never a number of its
        // own, or the button could offer a photo storage.rules then refuses.
        max: { type: Number, required: true },
        disabled: { type: Boolean, default: false }
    },
    data () { return { shrinking: false, error: '' } },
    methods: {
        open () {
            this.error = ''
            this.$refs.file.click()
        },
        async picked (e) {
            const file = e.target.files && e.target.files[0]
            // Cleared at once so the same photo can be chosen again after a
            // removal -- a file input does not fire `change` for an unchanged
            // value.
            e.target.value = ''
            if (!file || this.value.length >= this.max) return
            this.shrinking = true
            try {
                const blob = await shrink(file)
                this.$emit('input', this.value.concat([{ blob, url: URL.createObjectURL(blob) }]))
            } catch (err) {
                this.error = 'อ่านรูปไม่ได้ กรุณาถ่ายใหม่'
            }
            this.shrinking = false
        },
        remove (i) {
            const p = this.value[i]
            if (p) URL.revokeObjectURL(p.url)
            this.$emit('input', this.value.filter((_, j) => j !== i))
        },
        // Called by the parent when the sheet is reset for a new customer, so
        // the thumbnails' object URLs do not accumulate for the life of the tab.
        release () {
            this.value.forEach(p => URL.revokeObjectURL(p.url))
            this.error = ''
        }
    }
}
</script>
<style scoped>
.thumb { position: relative; }
.remove { position: absolute; top: -8px; right: -8px; }
</style>
